import {
  ChallengeContainer,
  ChallengeHeaderRow,
  ChallengeLoading,
  FlexChallengeTitle,
} from '@/components/challenge/components';
import { ChallengeBasicInfoFields } from '@/components/challenge/CompanyChallengeManagement/ChallengeBasicInfoFields';
import { ChallengeLabelsSection } from '@/components/challenge/CompanyChallengeManagement/ChallengeLabelsSection/ChallengeLabelsSection';
import {
  BLANK_VALUES,
  EMPTY_LABEL,
} from '@/components/challenge/CompanyChallengeManagement/constants';
import { CompanyChallengeSchema } from '@/components/challenge/CompanyChallengeManagement/schema';
import { IChallengeFormValues } from '@/components/challenge/CompanyChallengeManagement/types';
import { useCompanyChallengeInitialValues } from '@/hooks/useCompanyChallengeInitialValues';
import ConfirmModal, { ModalStatuses } from '@/components/dialogs/ConfirmModal';
import useAppDispatch from '@/hooks/useAppDispatch';
import useAppSelector from '@/hooks/useAppSelector';
import {
  resetCompanyChallenge,
  updateCompanyChallenge,
} from '@/redux/actions/challenge';
import {
  getChallengesLoading,
  getCompanyChallengeSelector,
} from '@/redux/selectors/challengeSelectors';
import { DEFAULT_TOAST_TIME } from '@/types/consts';
import { Button, CircularProgress } from '@mui/material';
import { Form, FormikHelpers, FormikProvider, useFormik } from 'formik';
import { FC, useState } from 'react';
import { toast } from 'react-toastify';

interface Props {
  companyId: number;
  companyName: string;
}

const CompanyChallengeManagement: FC<Props> = ({ companyId }) => {
  const dispatch = useAppDispatch();
  const challengesLoading = useAppSelector(getChallengesLoading);
  const companyChallenge = useAppSelector(getCompanyChallengeSelector);
  const isClearDataDisabled = !companyChallenge?.id;

  const { initialValues, maxUsedLabelIndex } =
    useCompanyChallengeInitialValues(companyId);

  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [isClearingData, setIsClearingData] = useState(false);

  const handleSubmit = async (
    values: IChallengeFormValues,
    { setSubmitting }: FormikHelpers<IChallengeFormValues>
  ) => {
    try {
      await dispatch(
        updateCompanyChallenge({
          companyId,
          title: values.title.trim(),
          description: values.description.trim() || null,
          labels: values.labels.map((label) => ({
            ...label,
            label: label.label.trim(),
          })),
        })
      ).unwrap();

      toast.success('Challenge configuration was successfully updated!', {
        position: 'top-right',
        autoClose: DEFAULT_TOAST_TIME,
        hideProgressBar: false,
      });
    } catch (err: any) {
      toast.error(err?.error ?? 'Failed to save changes', {
        position: 'top-right',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik<IChallengeFormValues>({
    initialValues: initialValues ?? BLANK_VALUES,
    enableReinitialize: true,
    validationSchema: CompanyChallengeSchema,
    onSubmit: handleSubmit,
  });

  const handleClearData = async () => {
    setIsClearingData(true);

    try {
      await dispatch(resetCompanyChallenge({ companyId })).unwrap();

      maxUsedLabelIndex.current = EMPTY_LABEL.index;
      formik.resetForm({ values: BLANK_VALUES });

      toast.success('Challenge data was cleared for every employee.', {
        position: 'top-right',
        autoClose: DEFAULT_TOAST_TIME,
        hideProgressBar: false,
      });
    } catch (err: any) {
      toast.error(err?.error ?? 'Failed to clear challenge data', {
        position: 'top-right',
      });
    } finally {
      setIsClearingData(false);
      setResetConfirmOpen(false);
    }
  };

  if (challengesLoading || !initialValues) {
    return (
      <ChallengeLoading>
        <CircularProgress />
      </ChallengeLoading>
    );
  }

  return (
    <FormikProvider value={formik}>
      <Form>
        <ChallengeContainer>
          <ChallengeHeaderRow>
            <FlexChallengeTitle variant="h2" textTransform="uppercase">
              Admin - Challenge Configuration
            </FlexChallengeTitle>
            <Button
              type="button"
              variant="outlined"
              color="secondary"
              onClick={() => setResetConfirmOpen(true)}
              disabled={isClearDataDisabled}
            >
              Clear Data
            </Button>
          </ChallengeHeaderRow>

          <ChallengeBasicInfoFields />

          <ChallengeLabelsSection maxUsedLabelIndexRef={maxUsedLabelIndex} />

          <ConfirmModal
            textConfirm="Clear Data"
            confirmStatus={ModalStatuses.WARNING}
            title="Warning"
            text="This will permanently delete all challenge data and user entries. Export the challenge report before continuing. This action cannot be undone."
            isOpen={resetConfirmOpen}
            onClose={() => setResetConfirmOpen(false)}
            onConfirm={handleClearData}
            loading={isClearingData}
            loadingText="Clearing Data..."
          />
        </ChallengeContainer>
      </Form>
    </FormikProvider>
  );
};

export default CompanyChallengeManagement;
